import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { Kcc20Candle } from '../../services/trading/kcc20-trading-api.service';
import {
  Kcc20ChartBar,
  Kcc20ChartMode,
  Kcc20PriceChartEngine,
  densifyBars,
  fmtCompact,
  fmtPrice,
  priceDecimals,
  toChartBar,
} from './kcc20-price-chart.engine';
import {
  KCC20_CHART_INTERVALS,
  KCC20_DEFAULT_CHART_INTERVAL_MS,
  Kcc20ChartIntervalMs,
} from './kcc20-chart-intervals';

/**
 * Trading price chart: candles / line / area, wheel + drag zoom, pan, and
 * timeframe switching.
 *
 * Rendering lives in {@link Kcc20PriceChartEngine} (plain canvas, outside
 * Angular) so the 60fps loop never triggers change detection. This component
 * owns inputs, the toolbar, and the OHLC readout only.
 *
 * The parent owns data loading: emit `intervalChange` -> refetch
 * `GET /trading/tokens/:tokenId/candles?intervalMs=...` -> push into `candles`.
 * `rangeRequest` fires when the user pans past the loaded history so the parent
 * can page older buckets in with `from`/`to`.
 */
@Component({
  selector: 'app-price-chart',
  templateUrl: './price-chart.component.html',
  styleUrls: ['./price-chart.component.scss'],
  imports: [DecimalPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PriceChartComponent {
  private readonly destroyRef = inject(DestroyRef);

  /** Raw candles from the trading API (sompi strings). */
  readonly candles = input<Kcc20Candle[]>([]);
  readonly intervalMs = input<Kcc20ChartIntervalMs>(
    KCC20_DEFAULT_CHART_INTERVAL_MS,
  );
  readonly loading = input(false);
  readonly title = input('Price');
  readonly subtitle = input('OHLCV · wrapped market');
  /** Quote symbol shown next to the last price. */
  readonly quoteSymbol = input('KAS');
  readonly showToolbar = input(true);

  readonly intervalChange = output<Kcc20ChartIntervalMs>();
  readonly modeChange = output<Kcc20ChartMode>();
  /** Emitted when the user pans beyond loaded history (epoch ms bounds). */
  readonly rangeRequest = output<{ from: number; to: number }>();

  private readonly hostRef =
    viewChild.required<ElementRef<HTMLDivElement>>('chartHost');

  protected readonly intervals = KCC20_CHART_INTERVALS;
  protected readonly modes: ReadonlyArray<{
    label: string;
    value: Kcc20ChartMode;
  }> = [
    { label: 'Candles', value: 'candles' },
    { label: 'Line', value: 'line' },
    { label: 'Area', value: 'area' },
  ];

  protected readonly mode = signal<Kcc20ChartMode>('line');
  protected readonly showVolume = signal(true);
  protected readonly visibleCount = signal(0);
  protected readonly hoverBar = signal<Kcc20ChartBar | null>(null);

  /**
   * Held in a signal (not a plain field) so the effects below re-run once the
   * engine exists instead of depending on constructor effect ordering.
   */
  private readonly engine = signal<Kcc20PriceChartEngine | null>(null);
  private readonly requestedRanges = new Set<string>();

  /** Bars in display space; also powers the header stats without re-parsing. */
  private readonly bars = computed<Kcc20ChartBar[]>(() => {
    const sorted = (this.candles() ?? [])
      .map(toChartBar)
      .filter((b) => Number.isFinite(b.c) && Number.isFinite(b.t))
      .sort((a, b) => a.t - b.t);
    return densifyBars(sorted, this.intervalMs());
  });

  protected readonly hasData = computed(() => this.bars().length > 0);
  protected readonly lastBar = computed(() => this.bars().at(-1) ?? null);

  /** Bar shown in the OHLC strip: hovered bar, else the newest one. */
  protected readonly readoutBar = computed(
    () => this.hoverBar() ?? this.lastBar(),
  );

  protected readonly decimals = computed(() => {
    const bars = this.bars();
    if (!bars.length) return 6;
    const lo = Math.min(...bars.map((b) => b.l));
    const hi = Math.max(...bars.map((b) => b.h));
    return priceDecimals(hi - lo || hi);
  });

  protected readonly lastPriceLabel = computed(() => {
    const last = this.lastBar();
    return last ? fmtPrice(last.c, this.decimals()) : '—';
  });

  /** Change across the loaded window (first real open -> last close). */
  protected readonly changePct = computed(() => {
    const bars = this.bars();
    if (bars.length < 2) return null;
    const firstReal = bars.find((b) => !b.empty) ?? bars[0];
    const open = firstReal.o;
    if (!open) return null;
    return ((bars[bars.length - 1].c - open) / open) * 100;
  });

  protected readonly changeLabel = computed(() => {
    const pct = this.changePct();
    if (pct === null) return null;
    return `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`;
  });

  protected readonly changeUp = computed(() => (this.changePct() ?? 0) >= 0);

  protected readonly totalVolumeLabel = computed(() =>
    fmtCompact(this.bars().reduce((sum, b) => sum + b.v, 0)),
  );

  protected readonly totalTrades = computed(() =>
    this.bars().reduce((sum, b) => sum + b.trades, 0),
  );

  constructor() {
    // DOM setup belongs in afterNextRender, not an effect: reading mode() /
    // showVolume() inside a creation effect would make every toolbar toggle
    // tear the canvas down and rebuild it, losing the user's zoom and pan.
    afterNextRender(() => {
      const engine = new Kcc20PriceChartEngine(this.hostRef().nativeElement, {
        mode: untracked(this.mode),
        showVolume: untracked(this.showVolume),
        onViewportChange: (state) => {
          this.visibleCount.set(Math.round(state.visibleCount));
          this.maybeRequestOlderRange(state.from);
        },
        onHover: (hover) => this.hoverBar.set(hover?.bar ?? null),
      });
      engine.setCandles(untracked(this.candles) ?? [], {
        intervalMs: untracked(this.intervalMs),
      });
      this.engine.set(engine);
      this.destroyRef.onDestroy(() => engine.destroy());
    });

    effect(() => {
      const engine = this.engine();
      const raw = this.candles() ?? [];
      const interval = this.intervalMs();
      if (!engine) return;
      // keepViewport lets a live poll land without yanking the user's zoom.
      engine.setCandles(raw, { intervalMs: interval, keepViewport: true });
      this.requestedRanges.clear();
    });

    effect(() => this.engine()?.setMode(this.mode()));

    effect(() => {
      const engine = this.engine();
      const visible = this.showVolume();
      if (engine) engine.showVolume = visible;
    });
  }

  protected onIntervalPick(value: Kcc20ChartIntervalMs): void {
    if (value === this.intervalMs()) return;
    this.intervalChange.emit(value);
  }

  protected setMode(value: Kcc20ChartMode): void {
    this.mode.set(value);
    this.modeChange.emit(value);
  }

  protected toggleVolume(): void {
    this.showVolume.update((v) => !v);
  }

  protected zoomIn(): void {
    this.engine()?.zoom(0.75);
  }
  protected zoomOut(): void {
    this.engine()?.zoom(1.35);
  }
  protected panLeft(): void {
    this.engine()?.panBy(-0.3);
  }
  protected panRight(): void {
    this.engine()?.panBy(0.3);
  }
  protected resetView(): void {
    this.engine()?.resetView();
  }
  protected fitAll(): void {
    this.engine()?.fitAll();
  }

  protected priceLabel(value: number): string {
    return fmtPrice(value, this.decimals());
  }

  protected volumeLabel(value: number): string {
    return fmtCompact(value);
  }

  /**
   * When the viewport reaches the left edge of loaded history, ask the parent
   * for the previous window. Guarded so a pan doesn't spam the API.
   */
  private maybeRequestOlderRange(from: number): void {
    if (from > 4) return;
    const bars = this.bars();
    if (!bars.length) return;
    const oldest = bars[0].t;
    const interval = this.intervalMs();
    const key = String(oldest);
    if (this.requestedRanges.has(key)) return;
    this.requestedRanges.add(key);
    this.rangeRequest.emit({ from: oldest - interval * 400, to: oldest });
  }
}
